import { useEffect, useMemo, useRef, useState } from 'react';
import {
  EMPTY,
  loadNavigationRegistry,
  matchRoute,
  normalisePath,
  peekNavigationRegistry,
} from '../services/navigationRegistry';

/**
 * useRouteGate — may this user open THIS URL (RBAC Phase 3).
 *
 * Phase 2 made page gating data, but only the menu honoured it: typing or
 * bookmarking a hidden page's URL still rendered the page. This hook is what
 * makes the gate real, by asking the same registry payload the sidebar reads
 * about the path in the address bar.
 *
 * **It is defence in depth, not the security boundary.** `HasModuleAccess`
 * still guards every endpoint, so a page reached past this gate shows no data
 * it should not. What this adds is an honest refusal — a panel naming the
 * missing codename — in place of an empty screen the user reports as broken.
 *
 * Statuses:
 *   `loading` — the payload is in flight (first protected page of the session)
 *   `allow`   — allowed, or gating is off/unconfigured
 *   `deny`    — the registry refuses this route for this user
 *
 * Everything that is not an explicit refusal is `allow`:
 *   - registry unconfigured, unmigrated, or the request failed
 *   - `enforce_routes` turned off for the tenant
 *   - the route has no registry entry (reported by `route-coverage` instead —
 *     default-deny on an incomplete registry locks users out of legitimate
 *     screens on day one), unless the tenant opted into `deny_unknown_routes`
 */
const useRouteGate = (pathname) => {
  const preloaded = peekNavigationRegistry();
  const [registry, setRegistry] = useState(preloaded || null);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    if (!registry) {
      loadNavigationRegistry().then((data) => {
        if (!cancelled.current) setRegistry(data);
      });
    }
    return () => {
      cancelled.current = true;
    };
  }, [registry]);

  return useMemo(() => {
    if (!registry) return { status: 'loading' };

    const { configured, routes, routeGates, enforcement } = registry || EMPTY;
    if (!configured || !enforcement || !enforcement.enforce_routes) {
      return { status: 'allow', reason: !configured ? 'unconfigured' : 'enforcement_off' };
    }

    const path = normalisePath(pathname);
    const matched = matchRoute(path, routes);

    if (!matched) {
      return enforcement.deny_unknown_routes
        ? { status: 'deny', path, reason: 'unknown_route', gate: null }
        : { status: 'allow', path, reason: 'unknown_route' };
    }

    const key = normalisePath(matched);
    if (routes[key]) return { status: 'allow', path, matched: key, reason: 'granted' };

    return {
      status: 'deny',
      path,
      matched: key,
      reason: 'denied',
      // Present for every denied route; the panel needs the codename to name.
      gate: (routeGates || {})[key] || null,
    };
  }, [registry, pathname]);
};

export default useRouteGate;
